import React from 'react'
import useSWRMutation from 'swr/mutation'
import useSWRImmutable from 'swr/immutable'

import { useI18N } from '../../../libraries/i18n/hook'
import { updateUserNewsPreferences } from '../../../api/users'
import type { UserPreferences } from '../../../api/users/types'
import useForm from '../../../libraries/form/hook'
import ErrorMessage from '../../components/errors/ErrorMessage'
import FormGroup from '../../components/form/FormGroup'
import ToggleButton from '../../components/form/ToggleButton'
import PhoneInput from '../../components/form/PhoneInput'
import FloatingSubmitButton from './FloatingSubmitButton'
import Checkbox from '../../components/form/Checkbox'
import { getAllCareerPaths } from '../../../api/career-paths'

type Props = { onSuccess?: () => void, defaultValues: Partial<UserPreferences> | undefined }
const NewsForm: React.FC<Props> = ({ onSuccess, defaultValues }) => {
  const { t } = useI18N()
  const { data: careerPaths, isLoading: isLoadingCareerPaths } = useSWRImmutable('career-paths', getAllCareerPaths)
  const { fields, error, submitting, handleChange, handleSubmit } = useForm<Partial<UserPreferences>, UserPreferences, '/users/preferences'>({
    initialValues: defaultValues ?? { favouriteCareerPaths: [] },
    mutation: useSWRMutation('/users/preferences', updateUserNewsPreferences),
    onSuccess,
  })

  const isUpdating = defaultValues?.onboardingCompleted

  const renderGroupCheckbox = (name: keyof UserPreferences, options: Array<{ value: string, label: string }>) => {
    return (
      <div className='inline-flex flex-wrap gap-4 justify-center items-center'>
        {options?.filter(option => (option.label !== 'Otro' && option.label !== 'Other')).map(option => {
          const checked = (fields[name] as string[]).map(item => item.toString())?.includes(option.value.toString())
          return (
            <Checkbox
              name={name}
              id={`${name}-${option.value}`}
              label={option.label}
              onChange={handleChange}
              key={`${name}-${option.value}`}
              value={option.value.toString()}
              checked={checked}
              required={(fields[name] as number[])?.length === 0}
            />
          )
        })}
      </div>
    )
  }

  const wsApChecked = fields.optinWhatsapp?.toString() === 'true'
  const jobsChecked = fields.optinJobOffers?.toString() === 'true'
  return (
    <form onSubmit={handleSubmit}>
      <FormGroup>
        <div className='mx-auto text-sm sm:text-base'>
          <ToggleButton
            name='optinJobOffers'
            id='optinJobOffers'
            label={t('onboarding.news.jobs.title')}
            onChange={handleChange}
            value={fields.optinJobOffers?.toString()}
            checked={jobsChecked}
          />
        </div>
      </FormGroup>
      {jobsChecked && !isLoadingCareerPaths && (
        <>
          <p className='text-xs sm:text-sm font-light text-center'>{t('onboarding.news.jobs.sectors')}</p>
          <FormGroup>
            {renderGroupCheckbox('favouriteCareerPaths', careerPaths?.map(careerPath => ({ value: careerPath.id, label: careerPath.name })) ?? [])}
          </FormGroup>
        </>
      )}
      <hr />
      <div className='max-w-[580px] mx-auto'>
        <FormGroup>
          <div className='mx-auto text-sm sm:text-base'>
            <ToggleButton
              name='optinWhatsapp'
              id='optinWhatsapp'
              label={t('onboarding.news.whatsapp.title')}
              onChange={handleChange}
              value={fields.optinWhatsapp?.toString()}
              checked={wsApChecked}
            />
          </div>
        </FormGroup>
        <FormGroup>
          <p className='text-xs sm:text-sm font-light text-center'>{t('onboarding.news.whatsapp.description')}</p>
        </FormGroup>
        {wsApChecked && (
          <FormGroup>
            <PhoneInput phoneCountryValue={fields.phoneCountryCode} phoneValue={fields.phone} placeholder={t('common.phone')} onChange={handleChange} required={wsApChecked} />
          </FormGroup>
        )}
      </div>
      {error && <ErrorMessage error={error} />}
      <FloatingSubmitButton disabled={submitting}>
        {isUpdating ? t('onboarding.update') : t('common.start.now')}
      </FloatingSubmitButton>
    </form>
  )
}

export default NewsForm
